import React from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Box, Flex, NakedButton } from '@qga/roo-ui/components';

import type { CheapestOffer } from '@shared/v2/utils/getPackageOptions/getPackageOptions';
import { Inclusion } from '@shared/types/client/ProcuredOffer';
import ToggleContent from '@shared/v2/components/ToggleContent';
import { ToggleContentProps } from '@shared/v2/components/ToggleContent/ToggleContent';
import { useDataLayerContext } from '@shared/v2/contexts/DataLayerContext';

import Button from '@/components/Button';
import Heading from '@/components/Heading';
import Text from '@/components/Text';
import useDestination from '@/hooks/useDestination';
import { brandColors } from '@/lib/theme/colors';
import {
  checkAvailabilityDataLayerEvent,
  viewPackageDetailsDataLayerEvent,
} from '@/v2/components/ui/AllPackageOptionsModal/utils/dataLayer';
import { Divider } from '@/v2/components/ui/Divider';
import {
  mapInclusion,
  PackageInclusions,
} from '@/v2/components/ui/PackageInclusions';
import { useDestination as v2UseDestination } from '@/v2/contexts/destination';
import { usePackagePageV2Analytics } from '@/v2/hooks/featureFlags';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import useSelectItemEvent from '@/v2/hooks/useSelectItemEvent';
import { dataLayer } from '@/v2/utils/dataLayer';
import LeadInOffer from './components/LeadInOffer';

export interface PackageOptionProps {
  id: string;
  title: string;
  propertyId: string;
  offer: CheapestOffer;
  onClickCTA?: () => void;
  onClickViewPackageDetailsAndTerms?: () => void;
  viewPackageDetailsAndTermsContent?: (
    overrideProps: object,
  ) => ToggleContentProps['content'];
  dataTestId?: string;
  inclusions: Array<Inclusion>;
  isPresearch: true;
  optionNumber: number;
  property: any;
}

const FlexGrow = styled(Flex)`
  max-width: 405px;
`;

const PackageOption = ({
  id,
  title,
  propertyId,
  onClickViewPackageDetailsAndTerms,
  viewPackageDetailsAndTermsContent,
  inclusions,
  offer,
  dataTestId = 'PACKAGE_OPTION',
  onClickCTA: handleAdditionalOnClickAction,
  isPresearch,
  optionNumber,
  property,
}: PackageOptionProps) => {
  const { pageName } = useDataLayerContext() || {};
  const { push, query } = useRouter();
  const shouldUseNewAnalytics = usePackagePageV2Analytics();
  const d = useDestination();
  const dV2 = v2UseDestination();
  const destination = d || dV2;
  const shouldUseNewEvents = useGA4Events();

  const name = destination?.theme ? title : `${title} holidays`;

  const selectItemEventData = useSelectItemEvent({
    originCode: offer?.cheapestRoute?.origin?.code,
    property,
    destination: property.destination?.title,
    name: name || property.destination?.name,
    price: offer?.cheapestRoute?.price?.total?.amount,
    index: optionNumber,
  });

  const onClickCTA = () => {
    handleAdditionalOnClickAction?.();
    if (optionNumber && pageName && shouldUseNewAnalytics) {
      checkAvailabilityDataLayerEvent({
        packageOptionNumber: optionNumber,
        pageName,
      });
    }

    const pathname = `/destinations/${destination?.name || property.destination.name}/properties/${propertyId}/package`;

    if (shouldUseNewEvents) {
      dataLayer.selectItemEvent(selectItemEventData);
      dataLayer.ctaClickEvent({
        itemText: 'Check availability',
        url: pathname,
      });
    }

    push({
      pathname,
      query: {
        ...query,
        packageOption: id,
      },
    });
  };
  const handleViewPackageDetailsAndTerms =
    (show: () => void) => (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (pageName && shouldUseNewAnalytics) {
        viewPackageDetailsDataLayerEvent({ pageName });
      }
      onClickViewPackageDetailsAndTerms?.();
      show();
    };
  const clickCTAText = isPresearch ? 'Check availability' : 'Select package';

  return (
    <Box
      boxShadow="elevation8"
      bg="background.card"
      padding={['16px', null, '24px']}
      data-testid={dataTestId}
      borderRadius="md"
      border="1"
      borderColor="lightGrey"
      overflow="hidden"
    >
      <Heading.h6
        data-testid={`${dataTestId}_TITLE`}
        color={brandColors.iron}
        lineHeight={['20px', null, '22px']}
        fontSize={['16px', null, '18px']}
      >
        {title}
      </Heading.h6>
      <Divider my={'16px'} />
      <Flex
        gap={'16px'}
        flexDirection={['column', null, 'row']}
        justifyContent={[null, null, 'space-between']}
      >
        <FlexGrow flexDirection="column" gap={'8px'}>
          <PackageInclusions
            inclusions={inclusions.map(mapInclusion)}
            numberToShow={4}
            showSubtitle={false}
          />
          {viewPackageDetailsAndTermsContent && (
            <ToggleContent
              toggle={(show) => (
                <NakedButton
                  textAlign="inherit"
                  data-testid={`${dataTestId}_TERMS_BUTTON`}
                  fontSize={['14px', null, '16px']}
                  lineHeight={['20px', null, '24px']}
                  onClick={handleViewPackageDetailsAndTerms(show)}
                  color="ui.link"
                  hoverColor="ui.linkHover"
                >
                  <Text bold link fontSize={['xs', null, 'sm']}>
                    View package details and terms
                  </Text>
                </NakedButton>
              )}
              content={viewPackageDetailsAndTermsContent({
                inclusions,
                minNights: offer.cheapestRoute.nights,
              })}
            />
          )}
        </FlexGrow>
        <Flex gap="16px" alignItems="flex-end" flexDirection="column">
          <LeadInOffer offer={offer} />
          {onClickCTA && (
            <Button
              dialogue
              noBorder
              block
              data-testid={`${dataTestId}_CTA_BUTTON`}
              width={['100%', null, '200px']}
              onClick={onClickCTA}
            >
              {clickCTAText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default PackageOption;
